import { encodeWav } from './WavEncoder.js';

/**
 * Getting a chosen file into a shape the chart engine can read.
 *
 * The rule is one sentence: **send the original bytes unless the engine cannot
 * decode them.** A WAV is uploaded exactly as it sits on disk; so is an MP3 or
 * an OGG when the engine reports it can read those. Only when the engine says it
 * cannot does the browser decode the file and write a fresh WAV.
 *
 * That ordering is deliberate. Re-encoding is lossy in the ways that matter to
 * an onset detector — a decode-and-requantise pass moves transients slightly and
 * changes the noise floor — so doing it "just to be safe" would degrade the
 * analysis for no reason. It also doubles the work on a 40 MB file.
 *
 * What the engine can decode is a property of the *installation*, not of the
 * format: WAV goes through Python's standard library, everything else needs
 * `soundfile`/libsndfile, which may or may not be installed. So the accepted set
 * is read from `/health` rather than assumed here.
 */

/** Formats a browser can be asked to open. */
export const AUDIO_FILE_ACCEPT = '.wav,.mp3,.ogg,.oga,.flac,.m4a,audio/*';

/** Decodes compressed audio. Supplied by the caller so this file needs no engine. */
export type AudioDecoder = (data: ArrayBuffer) => Promise<AudioBuffer>;

export interface PreparedAudio {
  readonly bytes: ArrayBuffer;
  readonly fileName: string;
  readonly mimeType: string;
  /** True when the browser decoded and rewrote the file. */
  readonly converted: boolean;
  readonly sourceFileName: string;
  readonly sourceBytes: number;
  /** Known only when the browser decoded it; the engine measures it otherwise. */
  readonly durationSeconds: number | null;
  readonly sampleRate: number | null;
  readonly channels: number | null;
  /** Human-readable account of what happened, for the status list. */
  readonly note: string;
}

export interface PrepareAudioOptions {
  /** Extensions the engine can decode, from `ChartEngineHealth.acceptedAudio`. */
  readonly accepted: readonly string[];
  readonly decode: AudioDecoder;
  readonly maxBytes?: number;
}

/** Anything with a name and bytes: a DOM `File`, or a stand-in in a test. */
export interface SelectedFile {
  readonly name: string;
  readonly size: number;
  readonly type: string;
  arrayBuffer(): Promise<ArrayBuffer>;
}

export async function prepareAudio(
  file: SelectedFile,
  options: PrepareAudioOptions,
): Promise<PreparedAudio> {
  if (file.size === 0) throw new Error(`"${file.name}" is empty.`);
  const limit = options.maxBytes;
  if (limit !== undefined && file.size > limit) {
    throw new Error(
      `"${file.name}" is ${formatMib(file.size)} and the engine accepts at most ${formatMib(limit)}.`,
    );
  }

  const extension = extensionOf(file.name);
  const accepted = new Set(options.accepted.map((value) => value.toLowerCase()));
  const bytes = await file.arrayBuffer();

  if (accepted.has(extension)) {
    return {
      bytes,
      fileName: file.name,
      mimeType: file.type.length > 0 ? file.type : mimeFor(extension),
      converted: false,
      sourceFileName: file.name,
      sourceBytes: file.size,
      durationSeconds: null,
      sampleRate: null,
      channels: null,
      note: `Sending ${extension || 'the file'} unchanged (${formatMib(file.size)}).`,
    };
  }

  // The engine cannot read this format on this machine, so the browser — which
  // already has a decoder for everything it can play — does the conversion.
  let decoded: AudioBuffer;
  try {
    // `decodeAudioData` detaches what it is given, so it gets a copy and the
    // original stays intact for the error message below.
    decoded = await options.decode(bytes.slice(0));
  } catch (cause) {
    throw new Error(
      `The chart engine cannot read ${extension || 'this file'} ` +
        `(it accepts ${options.accepted.join(', ') || 'nothing'}), and this browser could not ` +
        'decode it either. Convert it to WAV, or install soundfile for the engine.',
      { cause },
    );
  }

  const channels: Float32Array[] = [];
  for (let index = 0; index < decoded.numberOfChannels; index += 1) {
    channels.push(decoded.getChannelData(index));
  }
  const wav = encodeWav({ channels, sampleRate: decoded.sampleRate });
  return {
    bytes: wav,
    fileName: `${baseNameOf(file.name)}.wav`,
    mimeType: 'audio/wav',
    converted: true,
    sourceFileName: file.name,
    sourceBytes: file.size,
    durationSeconds: decoded.duration,
    sampleRate: decoded.sampleRate,
    channels: decoded.numberOfChannels,
    note:
      `Converted ${extension || 'the file'} to WAV in the browser ` +
      `(${decoded.numberOfChannels}ch ${decoded.sampleRate} Hz, ${formatMib(wav.byteLength)}) ` +
      'because the engine cannot decode it.',
  };
}

/**
 * Cover art, as Song Pack v1 wants it.
 *
 * Version 1 fixes the cover at `cover.png`, so a PNG is passed through and
 * anything else is re-drawn through a canvas. That conversion is not gratuitous
 * — it is the format requirement — but it is still only done when needed.
 */
export interface PreparedCover {
  readonly bytes: ArrayBuffer;
  readonly fileName: string;
  readonly mimeType: string;
  readonly converted: boolean;
  readonly note: string;
}

export const COVER_FILE_ACCEPT = '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp';

export async function prepareCover(file: SelectedFile): Promise<PreparedCover> {
  const bytes = await file.arrayBuffer();
  if (isPng(bytes)) {
    return {
      bytes,
      fileName: 'cover.png',
      mimeType: 'image/png',
      converted: false,
      note: `Cover ${file.name} is already PNG (${formatMib(file.size)}).`,
    };
  }
  const png = await toPng(bytes, file.type);
  return {
    bytes: png,
    fileName: 'cover.png',
    mimeType: 'image/png',
    converted: true,
    note: `Cover ${file.name} re-encoded to PNG, which is what Song Pack v1 stores.`,
  };
}

/** True when the bytes begin with the PNG signature. */
export function isPng(bytes: ArrayBuffer): boolean {
  const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.byteLength < signature.length) return false;
  const view = new Uint8Array(bytes, 0, signature.length);
  return signature.every((value, index) => view[index] === value);
}

async function toPng(bytes: ArrayBuffer, mimeType: string): Promise<ArrayBuffer> {
  const bitmap = await createImageBitmap(new Blob([bytes], { type: mimeType }));
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    if (context === null) throw new Error('A 2D canvas context could not be created.');
    context.drawImage(bitmap, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((value) => resolve(value), 'image/png'),
    );
    if (blob === null) throw new Error('The cover could not be encoded as PNG.');
    return blob.arrayBuffer();
  } finally {
    bitmap.close();
  }
}

/** Lower-case extension including the dot, or an empty string. */
export function extensionOf(name: string): string {
  const index = name.lastIndexOf('.');
  return index <= 0 ? '' : name.slice(index).toLowerCase();
}

function baseNameOf(name: string): string {
  const index = name.lastIndexOf('.');
  return index <= 0 ? name : name.slice(0, index);
}

function mimeFor(extension: string): string {
  switch (extension) {
    case '.wav':
      return 'audio/wav';
    case '.mp3':
      return 'audio/mpeg';
    case '.ogg':
    case '.oga':
      return 'audio/ogg';
    case '.flac':
      return 'audio/flac';
    default:
      return 'application/octet-stream';
  }
}

export function formatMib(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}
